import { useState, useEffect } from "react";
import { motion } from "framer-motion";

import IndustrySelector from "./IndustrySelector";
import ManagementExperienceSelector from "./ManagementExperienceSelector";
import EducationSelector from "./EducationSelector";
import TechnicalSkillsSelector from "./TechnicalSkillsSelector";
import SoftSkillsSelector from "./SoftSkillsSelector";
import WorkPreferenceSelector from "./WorkPreferenceSelector";
import FinancialRunwaySelector from "./FinancialRunwaySelector";
import AIComfortLevelSelector from "./AIComfortLevelSelector";
import OnetInterestProfiler from "../pages/OnetInterestProfiler";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthProvider";
import { apiFetch } from "../utils/apiFetch";
import { trackAttributedEventOnce } from "../utils/blogAnalytics";

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    if (e?.name === "QuotaExceededError") {
      console.warn("Storage full. Clearing old drafts...");

      Object.keys(localStorage)
        .filter((k) => k.startsWith("questionnaire_form_"))
        .forEach((k) => localStorage.removeItem(k));

      try {
        localStorage.setItem(key, value);
      } catch {}
    }
  }
}

export default function QuestionnaireStep1({ onComplete, plan = "snapshot"}) {
  const [userPlan, setUserPlan] = useState("snapshot");
  const isPremium = userPlan === "deep_audit" || plan === "deep_audit";

   // API requests are routed through apiFetch / onetApi.
  const { user: authUser, initialized } = useAuth();
  const authUid = authUser?.id || null;

  const getAssessmentKey = (uid) => `current_assessment_id_${uid}`;
  const qFormKey = (uid) => `questionnaire_form_${uid}`;
  const qStepKey = (uid) => `questionnaire_step_${uid}`;
  const qSubStepKey = (uid) => `questionnaire_substep_${uid}`;
  const qLastAnsweredKey = (uid) => `questionnaire_last_answered_${uid}`;

  useEffect(() => {
    console.log("✅ QuestionnaireStep1 mounted");
    return () => console.log("🧹 QuestionnaireStep1 unmounted");
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const TOTAL_STEPS = 6;

  const stepConfig = {
    1: ["roleIndustry", "locationAge"],
    2: ["experience"],
    3: ["education", "technical", "soft"],
    4: isPremium ? ["workPreference", "onet"] : ["workPreference"],
    5: ["financial", "ai"],
    6: ["done"],
  };

  const [lastAnsweredQuestionKey, setLastAnsweredQuestionKey] = useState(null);

  const getQuestionKey = (st, sub) => {
    const arr = stepConfig[st] || [];
    return arr[sub] || null;
  };

  const [step, setStep] = useState(1);
  const [subStep, setSubStep] = useState(0);
  const [error, setError] = useState("");
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [form, setForm] = useState({
    Current_Role_Title: "",
    Industry: [],
    Target_City: "",
    Age: null,
    Experience_Level: "",
    Physical_Constraints: "",
    Licenses_Certifications: "",
    Software_Tools: "",
    Management_Exp: [],
    Highest_Education: "",
    Technical_Skills: [],
    Soft_Skills: [],
    O_NET_Answers: {},
    O_NET_Answers: {},
    O_NET_Top_3: "",
    O_NET_Scores: {},
    onetResult: null,
    Work_Preferences: [],
    Work_Environment: [],
    Financial_Runway: "",
    AI_Comfort_Level: [],
  });

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ===========================================================
      ✅ PART 1 — Check Session (auth-ready + redirect)
     =========================================================== */
  useEffect(() => {
    if (!initialized) return;

    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) window.location.href = "/login";
    };

    checkSession();
  }, [initialized]);

  /* ===========================================================
      ✅ PART 2 — Restore Draft (PER USER)
      ✅ Backend restore ONLY if assessmentId exists
     =========================================================== */
  useEffect(() => {
  const restoreDraft = async () => {
    if (!initialized) return;
    if (!authUid) return;

    // 1) Determine plan (single source of truth)
    let resolvedPlan = "snapshot";

    try {
      const { data: dbUser } = await supabase
        .from("users")
        .select("is_premium")
        .eq("auth_uid", authUid)
        .maybeSingle();

      if (dbUser?.is_premium === true) resolvedPlan = "deep_audit";
    } catch (e) {
      console.warn("⚠ Plan lookup failed, using fallback:", e?.message || e);
    }

    // optional fallback
    const storedPlan = localStorage.getItem(`selectedPlan_${authUid}`);
    if (storedPlan === "snapshot" || storedPlan === "deep_audit") {
      resolvedPlan = storedPlan;
    }

    setUserPlan(resolvedPlan);
    localStorage.setItem(`selectedPlan_${authUid}`, resolvedPlan);

    const assessmentId = localStorage.getItem(getAssessmentKey(authUid));
    let activeAssessmentId = assessmentId;

    // ✅ create assessment row if missing
    if (!activeAssessmentId) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (token) {
          const res =
  await apiFetch(
    "/api/careerpivot/start-assessment", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const created = await res.json();
            if (created?.assessmentId) {
              localStorage.setItem(getAssessmentKey(authUid), created.assessmentId);
              activeAssessmentId = created.assessmentId;

            }
          }
        }
      } catch (err) {
        console.warn("Failed to create assessment:", err.message);
      }
    }

    // ============================================================
    // PHASE 6C — ASSESSMENT STARTED
    // Works for BOTH:
    // 1) a newly-created assessment
    // 2) an assessment ID that already existed in localStorage
    //
    // trackAttributedEventOnce prevents duplicate GA4 events
    // for the same assessment ID.
    // ============================================================
    if (activeAssessmentId) {
      const sent = trackAttributedEventOnce(
        "assessment_started",
        activeAssessmentId,
        {
          assessmentId: activeAssessmentId,
          reportType: resolvedPlan,
        }
      );

      console.log("[Phase6C] assessment_started check", {
        assessmentId: activeAssessmentId,
        reportType: resolvedPlan,
        sent,
      });
    }

    // helper: snapshot users must never land on O*NET substep
    const clampForSnapshot = (nextStep, nextSub) => {
      if (resolvedPlan !== "deep_audit") {
        if (Number(nextStep) === 4 && Number(nextSub) === 1) {
          return { step: 4, subStep: 0 };
        }
      }
      return { step: Number(nextStep) || 1, subStep: Number(nextSub) || 0 };
    };

    // 2) LOCAL-FIRST restore.
    // localStorage is written immediately while the backend draft is debounced,
    // so after a refresh the local copy is usually the freshest source.
    const savedForm = localStorage.getItem(qFormKey(authUid));
    const savedStep = localStorage.getItem(qStepKey(authUid));
    const savedSubStep = localStorage.getItem(qSubStepKey(authUid));
    const savedLastAnswered = localStorage.getItem(qLastAnsweredKey(authUid));

    if (savedForm) {
      try {
        const parsedLocal = safeParseForm(savedForm);

        setForm((prev) => ({
          ...prev,
          ...parsedLocal,
          O_NET_Answers: parsedLocal?.O_NET_Answers || {},
          O_NET_Scores: parsedLocal?.O_NET_Scores || {},
          onetResult: parsedLocal?.onetResult || null,
        }));

        setLastAnsweredQuestionKey(savedLastAnswered || null);

        const fixed = clampForSnapshot(savedStep || 1, savedSubStep || 0);
        setStep(fixed.step);
        setSubStep(fixed.subStep);

        console.log("✅ Draft restored from localStorage (freshest browser copy)");
        setDraftLoaded(true);
        return;
      } catch (err) {
        console.warn("⚠ Local draft restore failed:", err?.message || err);
      }
    }

    // 3) Backend fallback only when there is no usable local draft.
    if (activeAssessmentId && navigator.onLine) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (token) {
          const res = await apiFetch(
            `/api/careerpivot/get-draft/${activeAssessmentId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (res.ok) {
            const data = await res.json();

            if (data?.payloadJson) {
              const backendForm = safeParseForm(data.payloadJson);

              setForm((prev) => ({
                ...prev,
                ...backendForm,
                O_NET_Answers: backendForm?.O_NET_Answers || {},
                O_NET_Scores: backendForm?.O_NET_Scores || {},
                onetResult: backendForm?.onetResult || null,
              }));

              setLastAnsweredQuestionKey(data.lastAnsweredQuestionKey || null);

              const fixed = clampForSnapshot(data.step || 1, data.subStep || 0);
              setStep(fixed.step);
              setSubStep(fixed.subStep);

              console.log("✅ Draft restored from backend fallback");
              setDraftLoaded(true);
              return;
            }
          }
        }
      } catch (err) {
        console.warn("⚠ Backend restore failed:", err?.message || err);
      }
    }

    // 4) Nothing to restore: start at the beginning.
    setStep(1);
    setSubStep(0);
    setLastAnsweredQuestionKey(null);
    setDraftLoaded(true);
  };

  restoreDraft();
}, [initialized, authUid]);  /* ===========================================================
      ✅ PART 3 — LocalStorage Autosave PER USER
     =========================================================== */
  useEffect(() => {
  if (!draftLoaded) return;
  if (!authUid) return;

  safeSetItem(qFormKey(authUid), JSON.stringify(form));
  safeSetItem(qStepKey(authUid), step.toString());
  safeSetItem(qSubStepKey(authUid), subStep.toString());

  if (lastAnsweredQuestionKey) {
    safeSetItem(qLastAnsweredKey(authUid), lastAnsweredQuestionKey);
  }
}, [form, step, subStep, lastAnsweredQuestionKey, draftLoaded, authUid]);
  /* ===========================================================
      ✅ PART 4 — Save Draft to Backend ONLY if assessmentId exists (PER USER)
     =========================================================== */
  const saveDraftToBackend = async (answeredKeyOverride = null) => {
  try {
    if (!navigator.onLine) {
      console.log("📴 Offline — draft is safe in localStorage and will sync after reconnect.");
      return;
    }

    if (!authUid) return;

    const assessmentId = localStorage.getItem(getAssessmentKey(authUid));
    if (!assessmentId) return;

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return;

    const hasAnyData = Object.values(form).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "string") return val.trim().length > 0;
      return val !== null;
    });

    if (!hasAnyData) return;

    await apiFetch(
  "/api/careerpivot/save-draft", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        assessmentId,
        payloadJson: form,
        step,
        subStep,
        currentQuestionKey: getQuestionKey(step, subStep),
        lastAnsweredQuestionKey: answeredKeyOverride ?? lastAnsweredQuestionKey,
        questionnaireStatus: "in_progress",
        exitReason: null,
      }),
    });
  } catch (err) {
    console.warn("⚠ Draft save failed:", err.message);
  }
};
const markExit = async (reason) => {
  try {
    if (!authUid) return;

    const assessmentId = localStorage.getItem(getAssessmentKey(authUid));
    if (!assessmentId) return;

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return;

    await apiFetch(
  "/api/careerpivot/mark-exit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        assessmentId,
        step,
        subStep,
        currentQuestionKey: getQuestionKey(step, subStep),
        lastAnsweredQuestionKey,
        questionnaireStatus:
          reason === "logout" ? "logged_out_midway" : "left_to_dashboard",
        exitReason: reason,
      }),
    });
  } catch (err) {
    console.warn("markExit failed:", err.message);
  }
};
  /* ===========================================================
      ✅ PART 5 — Debounced Backend autosave
     =========================================================== */
  useEffect(() => {
    if (!draftLoaded) return;

    const timeout = setTimeout(() => {
      saveDraftToBackend();
    }, 1500);

    return () => clearTimeout(timeout);
  }, [form, step, subStep, draftLoaded]);

  // When connectivity returns, immediately sync the latest locally-saved draft.
  useEffect(() => {
    if (!isOnline || !draftLoaded || !authUid) return;
    saveDraftToBackend();
    // saveDraftToBackend intentionally omitted from dependencies to avoid
    // recreating this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, draftLoaded, authUid]);

  const sanitizeDraftForPlan = (payload, st, sub) => {
  if (isPremium) return { payload, st, sub };

  // Snapshot: remove O*NET field
  const cleaned = {
    ...payload,
    O_NET_Top_3: "",
    O_NET_Scores: {},
    onetResult: null,
  };

  // Snapshot: if draft points to O*NET step, push back to WorkPreference
  if (st === 4 && sub === 1) {
    return { payload: cleaned, st: 4, sub: 0 };
  }

  return { payload: cleaned, st, sub };
};
const safeParseForm = (value) => {
  if (!value) return {};

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      let parsed = JSON.parse(value);

      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }

      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
};
  /* ===========================================================
      ✅ PART 6 — SUBMIT FINAL QUESTIONNAIRE (PER USER assessmentId)
     =========================================================== */
  const submitQuestionnaire = async () => {
    if (!authUid) {
      alert("Auth user missing. Please login again.");
      return false;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      alert("No token. Please login again.");
      return false;
    }

    try {
      const finalPlan =
  userPlan === "deep_audit" || plan === "deep_audit"
    ? "deep_audit"
    : "snapshot";

const cleanForm = safeParseForm(form);

const payload = {
  plan: finalPlan,
  ...cleanForm,
};

const res = await apiFetch ("/api/careerpivot/submit-assessment", {
  method: "POST",
  body: JSON.stringify(payload),
});      const raw = await res.text();

      if (!res.ok) {
        alert("Submit failed: " + raw);
        return false;
      }

const data = raw ? JSON.parse(raw) : {};

// ============================================================
// PHASE 6C — ASSESSMENT COMPLETED
//
// Backend may or may not return assessmentId.
// If it doesn't, use the assessment ID already stored for user.
// ============================================================

const completedAssessmentId =
  data?.assessmentId ||
  localStorage.getItem(getAssessmentKey(authUid));

if (completedAssessmentId) {
  // Keep the current assessment ID available for later
  // report-generation/payment steps.
  localStorage.setItem(
    getAssessmentKey(authUid),
    completedAssessmentId
  );

  const sent = trackAttributedEventOnce(
    "assessment_completed",
    completedAssessmentId,
    {
      assessmentId: completedAssessmentId,
      reportType: finalPlan,
    }
  );

  console.log(
    "[Phase6C] assessment_completed check",
    {
      assessmentId: completedAssessmentId,
      reportType: finalPlan,
      sent,
    }
  );
} else {
  console.warn(
    "[Phase6C] assessment_completed skipped: no assessment ID available"
  );
}
      localStorage.removeItem(qFormKey(authUid));
      localStorage.removeItem(qStepKey(authUid));
      localStorage.removeItem(qSubStepKey(authUid));

      return true;
    } catch (e) {
      alert("Submit failed: " + (e?.message || e));
      return false;
    }
  };

  const validateCurrentStep = () => {
    setError("");

    // STEP 1 — SubStep 0: Role + Industry
    if (step === 1 && subStep === 0) {
      if (!form.Current_Role_Title?.trim()) {
        setError("Please enter your Current Job Title.");
        return false;
      }
      if (!form.Industry || form.Industry.length === 0) {
        setError("Please select an Industry.");
        return false;
      }
      return true;
    }

    // STEP 1 — SubStep 1: Location + Age
    if (step === 1 && subStep === 1) {
      if (!form.Target_City?.trim()) {
        setError("Please enter your preferred City / Location.");
        return false;
      }
      const age = Number(form.Age);
      if (!age || age < 14 || age > 90) {
        setError("Please enter a valid age (14 - 90).");
        return false;
      }
      return true;
    }

    // STEP 2 — Experience Level
    if (step === 2) {
      if (!form.Experience_Level) {
        setError("Please select your Experience Level.");
        return false;
      }

      if (form.Experience_Level === "Blue") {
        if (!form.Physical_Constraints?.trim() && !form.Licenses_Certifications?.trim()) {
          setError("Please fill Physical Constraints or Licenses/Certifications.");
          return false;
        }
      }

      if (form.Experience_Level === "White" || form.Experience_Level === "Executive") {
        if (!form.Software_Tools?.trim()) {
          setError("Please list the Software Tools you can use.");
          return false;
        }
      }

      return true;
    }

    // STEP 3 — Education / Technical / Soft
    if (step === 3 && subStep === 0) {
      if (!form.Highest_Education) {
        setError("Please select your Highest Education.");
        return false;
      }
      return true;
    }

    if (step === 3 && subStep === 1) {
      if (!form.Technical_Skills || form.Technical_Skills.length === 0) {
        setError("Please select at least one Technical Skill.");
        return false;
      }
      return true;
    }

    if (step === 3 && subStep === 2) {
      if (!form.Soft_Skills || form.Soft_Skills.length === 0) {
        setError("Please select at least one Soft Skill.");
        return false;
      }
      return true;
    }

    // STEP 4 — Work prefs (+ O*NET for premium)
    if (step === 4 && subStep === 0) {
      if (!form.Work_Preferences || form.Work_Preferences.length === 0) {
        setError("Please select your Work Preferences.");
        return false;
      }
      if (!form.Work_Environment || form.Work_Environment.length === 0) {
        setError("Please select your preferred Work Environment.");
        return false;
      }
      return true;
    }

    if (isPremium && step === 4 && subStep === 1) {
      if (!form.onetResult || !form.O_NET_Top_3) {
        setError("Please complete the O*NET Interest Profiler before continuing.");
        return false;
      }
      return true;
    }

    // STEP 5 — Financial + AI comfort
    if (step === 5 && subStep === 0) {
      if (!form.Financial_Runway) {
        setError("Please select your Financial Runway.");
        return false;
      }
      return true;
    }

    if (step === 5 && subStep === 1) {
      if (!form.AI_Comfort_Level || form.AI_Comfort_Level.length === 0) {
        setError("Please select your AI Comfort Level.");
        return false;
      }
      return true;
    }

    return true;
  };

  /* ===========================================================
      ✅ NEXT / BACK
     =========================================================== */
       const goNext = async () => {
  if (!validateCurrentStep()) return;

  const answeredKey = getQuestionKey(step, subStep);
  setLastAnsweredQuestionKey(answeredKey);
  safeSetItem(qLastAnsweredKey(authUid), answeredKey);

  await saveDraftToBackend(answeredKey);

  const subSteps = stepConfig[step];

  if (subStep < subSteps.length - 1) {
    setSubStep((s) => s + 1);
  } else {
    setSubStep(0);
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
    } else {
      const ok = await submitQuestionnaire();
      if (ok) onComplete();
    }
  }
};  const goBack = () => {
    setError("");

    if (subStep > 0) {
      setSubStep((s) => s - 1);
      return;
    }

    if (step > 1) {
      const prevStep = step - 1;
      const prevSubSteps = stepConfig[prevStep] || [];
      const lastPrevSubStep = Math.max(prevSubSteps.length - 1, 0);

      setStep(prevStep);
      setSubStep(lastPrevSubStep);
      return;
    }

    setStep(1);
    setSubStep(0);
  };

  /* ===========================================================
      ✅ HOLD UI until restore completes
     =========================================================== */
  if (!draftLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center text-pink-400">
        Restoring your progress...
      </div>
    );
  }

  /* ===========================================================
      ✅ UI
     =========================================================== */
  const progressPercent =
    ((step - 1 + (subStep + 1) / stepConfig[step].length) / TOTAL_STEPS) * 100;

  const titleClass = "text-xl font-bold text-pink-400";
  const titleToContentGap = "mt-5";
  const smallGap = "mt-3";
  const inputClass = "w-full p-3 bg-[#1b1b2b] rounded border border-pink-400/40";

  return (
    <motion.div
      key={`${step}-${subStep}`}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      // ✅ CRITICAL: make this a flex column + fill parent height + allow inner scroll areas
      className="bg-[#141428]/90 border border-[#EB6AB5]/20 rounded-2xl shadow-2xl backdrop-blur-md text-white
                 flex flex-col h-full min-h-0"
    >
      {/* ✅ TOP (fixed) */}
      <div className="px-4 sm:px-8 pt-6">
        {/* PROGRESS */}
        <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
          <div
            className="h-2 rounded-full bg-pink-500 transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* ✅ MIDDLE (scrollable) */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 pb-4">
        {!isOnline && (
          <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-300">
            You're offline. Your answers are saved on this device and will sync automatically when your connection returns.
          </div>
        )}

        {/* STEP 1 — Role + Industry */}
        {step === 1 && subStep === 0 && (
          <div>
            <h2 className={titleClass}>👤 Your professional identity</h2>

            <div className={titleToContentGap}>
              <input
                placeholder="Your Current Job Title"
                value={form.Current_Role_Title}
                onChange={(e) => updateField("Current_Role_Title", e.target.value)}
                className={inputClass}
              />

              <div className={smallGap}>
                <IndustrySelector form={form} updateField={updateField} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 1 — City + Age */}
        {step === 1 && subStep === 1 && (
          <div>
            <h2 className={titleClass}>📍 Location & age (Optional)</h2>

            <div className={titleToContentGap}>
              <input
                placeholder="Which City, Country do you prefer for localisation? (One location only)"
                value={form.Target_City}
                onChange={(e) => updateField("Target_City", e.target.value)}
                className={inputClass}
              />

              <input
                type="number"
                placeholder="What is your age?"
                value={form.Age || ""}
                onChange={(e) =>
                  updateField("Age", e.target.value ? Number(e.target.value) : null)
                }
                className={`${inputClass} ${smallGap}`}
              />
            </div>
          </div>
        )}

        {/* STEP 2 — EXPERIENCE */}
        {step === 2 && (
          <div>
            <h2 className={titleClass}>🧰 Which best describes your role?</h2>

            <div className={titleToContentGap}>
              <select
                value={form.Experience_Level}
                onChange={(e) => updateField("Experience_Level", e.target.value)}
                className={inputClass}
              >
                <option value="">Select</option>
                <option value="Student"> Student [Student / Starting Out (No professional experience yet; currently in school or a recent grad)]</option>
                <option value="Blue">Blue Collar[Service, Site, or Trade (The job requires being physically onsite (e.g., Retail, Construction, Medical Staff, Hospitality, or Drivers)]</option>
                <option value="White">White Collar[Office, Tech, or Sales - You primarily work on a computer, even if you travel for work (e.g., Account Managers, Admin, or Software Developers)]</option>
                <option value="Executive">Executive[Senior Leadership - You manage other managers or set company-wide strategy (e.g., Directors, VPs, or Business Owners).]</option>
              </select>

              {form.Experience_Level === "Blue" && (
                <>
                  <textarea
                    placeholder="Do you have any Physical Constraints?"
                    value={form.Physical_Constraints}
                    onChange={(e) => updateField("Physical_Constraints", e.target.value)}
                    className={`${inputClass} ${smallGap}`}
                  />

                  <textarea
                    placeholder="You can add any Licenses or Certifications you have here"
                    value={form.Licenses_Certifications}
                    onChange={(e) => updateField("Licenses_Certifications", e.target.value)}
                    className={`${inputClass} ${smallGap}`}
                  />
                </>
              )}

              {(form.Experience_Level === "White" || form.Experience_Level === "Executive") && (
                <>
                  <textarea
                    placeholder="List any Software Tools you can use in your daily work (MS Office, CRM tools, Accounting Packages , ERP (SAP / Oracle) Creative suites (Illustrator, Canva etc) "
                    value={form.Software_Tools}
                    onChange={(e) => updateField("Software_Tools", e.target.value)}
                    className={`${inputClass} ${smallGap}`}
                  />

                  <div className={smallGap}>
                    <ManagementExperienceSelector form={form} updateField={updateField} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && subStep === 0 && (
          <div>
            <h2 className={titleClass}>🎓 Highest education</h2>
            <div className={titleToContentGap}>
              <EducationSelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {step === 3 && subStep === 1 && (
          <div>
            <h2 className={titleClass}>🛠️ Core Expertise & Professional Tools (Select up to 3 areas where you have the most hands-on experience. You can choose from the list or type a specific skill below. Total limit: 3)</h2>
            <div className={titleToContentGap}>
              <TechnicalSkillsSelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {step === 3 && subStep === 2 && (
          <div>
            <h2 className={titleClass}>🤝 Your Human Strengths (Select the 3 that define how you work with others.You can also add unique strengths like "Empathy and Emotional Intelligence" etc. that's not listed, by clicking the "add" button below. (Total Limit: 3)</h2>
            <div className={titleToContentGap}>
              <SoftSkillsSelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && subStep === 0 && (
          <div>
            <h2 className={titleClass}>💼 Work preferences</h2>
            <div className={titleToContentGap}>
              <WorkPreferenceSelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {isPremium && step === 4 && subStep === 1 && (
          <div>
            <h2 className={titleClass}>🧩 O*NET Interest Profiler</h2>

            <div className={titleToContentGap}>
              <OnetInterestProfiler
                initialAnswers={form.O_NET_Answers || {}}
                initialResult={form.onetResult || null}
                onAnswersChange={(answers) => {
                  setForm((prev) => ({
                    ...prev,
                    O_NET_Answers: answers,
                  }));
                }}
                onComplete={async (result) => {
                  const top3 = Array.isArray(result?.top3) ? result.top3 : [];

                  // Preserve the existing backend/report contract by keeping
                  // O_NET_Top_3 as a compact RIASEC code string (example: SA).
                  // The complete six-area scores are stored separately.
                  const topCode = top3
                    .slice(0, 2)
                    .map((item) => String(item || "").charAt(0).toUpperCase())
                    .join("");

                  setForm((prev) => ({
                    ...prev,
                    onetResult: result,
                    O_NET_Top_3: topCode,
                    O_NET_Scores: result?.scores || {},
                  }));

                  setLastAnsweredQuestionKey("onet");
                  setError("");
                }}
              />

              {form.O_NET_Top_3 && (
                <div className="mt-4 rounded-xl border border-emerald-400/20 bg-emerald-400/5 p-4">
                  <p className="text-sm font-semibold text-emerald-300">
                    ✓ O*NET profile completed
                  </p>
                  <p className="mt-1 text-xs text-white/55">
                    Interest code saved: {form.O_NET_Top_3}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && subStep === 0 && (
          <div>
            <h2 className={titleClass}>💰 Financial runway</h2>
            <div className={titleToContentGap}>
              <FinancialRunwaySelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {step === 5 && subStep === 1 && (
          <div>
            <h2 className={titleClass}>🤖 AI comfort level</h2>
            <div className={titleToContentGap}>
              <AIComfortLevelSelector form={form} updateField={updateField} />
            </div>
          </div>
        )}

        {/* STEP 6 */}
        {step === 6 && (
          <div className="text-center">
            <h2 className={titleClass}>✅ All questions completed</h2>
            <p className="text-gray-300 mt-2">Click continue to proceed.</p>
          </div>
        )}

        {/* ✅ ERROR MESSAGE (inside scroll area) */}
        {error && (
          <p className="text-red-400 text-sm font-semibold mt-4 text-center">
            ⚠️ {error}
          </p>
        )}
      </div>

      {/* ✅ BOTTOM NAV (fixed / pinned) */}
      <div className="px-4 sm:px-8 py-4 border-t border-[#EB6AB5]/10 bg-[#141428]/80 backdrop-blur">
        <div className="flex justify-center gap-6">
          <button
            disabled={step === 1 && subStep === 0}
            onClick={goBack}
            className="px-6 py-2 bg-gray-700 rounded disabled:opacity-40"
          >
            Back
          </button>

          <button onClick={goNext} className="px-6 py-2 bg-pink-500 text-black rounded">
            {step === TOTAL_STEPS ? "Continue" : "Next"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}